import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faFileArrowUp,
  faSpinner,
  faXmark,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faVideo,
  faFile,
  faImage,
  faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast, swalConfirm } from '@/lib/swal';
import {
  useStepEvidenceList,
  useUploadEvidence,
  useDeleteEvidence,
  useReplaceEvidence,
} from '../../hooks/useStepEvidence';
import type { EvidenceItem, EvidenceType } from '../../services/procedure-execution.service';

function fmtBytes(v: string): string {
  const n = parseInt(v, 10);
  if (isNaN(n)) return '';
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1_048_576).toFixed(1)} MB`;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

// ── Image Lightbox ────────────────────────────────────────────────────────────

function EvidenceLightbox({
  items,
  startId,
  onClose,
}: {
  items: EvidenceItem[];
  startId: string;
  onClose: () => void;
}) {
  const images = items.filter((i) => i.evidenceType === 'IMAGE');
  const [idx, setIdx] = useState(() => {
    const i = images.findIndex((img) => img.id === startId);
    return i >= 0 ? i : 0;
  });

  const img = images[idx];
  if (!img) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Evidence viewer"
    >
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-sm text-white/60 select-none">
        {idx + 1} / {images.length}
      </div>
      <button
        className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center text-white/80 hover:text-white transition"
        onClick={onClose}
        aria-label="Close"
      >
        <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
      </button>
      <a
        href={img.signedUrl}
        download={img.fileName}
        className="absolute top-4 right-16 flex h-10 w-10 items-center justify-center text-white/80 hover:text-white transition"
        onClick={(e) => e.stopPropagation()}
        aria-label="Download"
      >
        <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
      </a>
      {idx > 0 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center text-white/80 hover:text-white transition"
          onClick={(e) => {
            e.stopPropagation();
            setIdx((c) => c - 1);
          }}
          aria-label="Previous"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-6 w-6" />
        </button>
      )}
      {idx < images.length - 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center text-white/80 hover:text-white transition"
          onClick={(e) => {
            e.stopPropagation();
            setIdx((c) => c + 1);
          }}
          aria-label="Next"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-6 w-6" />
        </button>
      )}
      <div className="max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
        <img
          src={img.signedUrl}
          alt={img.fileName}
          className="max-h-[90vh] max-w-[90vw] object-contain"
        />
      </div>
    </div>,
    document.body,
  );
}

// ── Video Modal ───────────────────────────────────────────────────────────────

function VideoModal({ item, onClose }: { item: EvidenceItem; onClose: () => void }) {
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center text-white/80 hover:text-white transition"
        onClick={onClose}
        aria-label="Close"
      >
        <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
      </button>
      <div className="max-h-[85vh] max-w-full" onClick={(e) => e.stopPropagation()}>
        <video
          src={item.signedUrl}
          controls
          autoPlay
          className="max-h-[85vh] max-w-[90vw] rounded-lg"
        />
      </div>
    </div>,
    document.body,
  );
}

// ── Evidence Type Row ─────────────────────────────────────────────────────────

interface EvidenceTypeRowProps {
  label: string;
  icon: typeof faImage;
  evidenceType: EvidenceType;
  items: EvidenceItem[];
  minCount: number;
  canUpload: boolean;
  isCapture: boolean;
  accept: string;
  onUpload: (file: File) => void;
  onDelete: (id: string) => void;
  onReplace: (id: string, file: File) => void;
  uploadingId: string | null;
  deletingId: string | null;
  replacingId: string | null;
}

function EvidenceTypeRow({
  label,
  icon,
  evidenceType,
  items,
  minCount,
  canUpload,
  isCapture,
  accept,
  onUpload,
  onDelete,
  onReplace,
  uploadingId,
  deletingId,
  replacingId,
}: EvidenceTypeRowProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  const [pendingReplaceId, setPendingReplaceId] = useState<string | null>(null);

  const [lightboxId, setLightboxId] = useState<string | null>(null);
  const [videoItem, setVideoItem] = useState<EvidenceItem | null>(null);

  function handleReplaceSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f && pendingReplaceId) onReplace(pendingReplaceId, f);
    setPendingReplaceId(null);
    e.target.value = '';
  }

  const count = items.length;
  const metMinimum = minCount === 0 || count >= minCount;
  const badgeClass =
    minCount > 0
      ? metMinimum
        ? 'bg-green-600/10 text-green-700'
        : 'bg-amber-500/10 text-amber-700'
      : 'bg-muted text-muted-foreground';
  const badgeLabel = minCount > 0 ? `${count} / ${minCount} required` : `${count} uploaded`;

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) onUpload(f);
    e.target.value = '';
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-foreground">{label}</span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>
            {badgeLabel}
          </span>
        </div>
        {canUpload && (
          <div className="flex items-center gap-1.5">
            {isCapture && (
              <>
                <input
                  ref={cameraRef}
                  type="file"
                  accept={accept}
                  capture="environment"
                  className="hidden"
                  onChange={handleFile}
                />
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  disabled={uploadingId === evidenceType}
                  className="flex h-8 min-w-[44px] items-center justify-center gap-1 rounded-md border border-border px-2 text-xs text-foreground hover:bg-muted disabled:opacity-50 transition"
                  aria-label="Take photo"
                >
                  {uploadingId === evidenceType ? (
                    <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faCamera} className="h-3 w-3" />
                  )}
                  <span className="hidden sm:inline">Take</span>
                </button>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={accept}
              className="hidden"
              onChange={handleFile}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingId === evidenceType}
              className="flex h-8 min-w-[44px] items-center justify-center gap-1 rounded-md border border-border px-2 text-xs text-foreground hover:bg-muted disabled:opacity-50 transition"
              aria-label="Upload file"
            >
              {uploadingId === evidenceType ? (
                <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faFileArrowUp} className="h-3 w-3" />
              )}
              <span className="hidden sm:inline">Upload</span>
            </button>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="relative group rounded-lg overflow-hidden border border-border bg-muted/30"
            >
              {item.evidenceType === 'IMAGE' ? (
                <button
                  type="button"
                  className="w-full aspect-square"
                  onClick={() => setLightboxId(item.id)}
                  aria-label={`View ${item.fileName}`}
                >
                  <img
                    src={item.signedUrl}
                    alt={item.fileName}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </button>
              ) : item.evidenceType === 'VIDEO' ? (
                <button
                  type="button"
                  className="w-full aspect-square flex items-center justify-center bg-black/5 hover:bg-black/10 transition"
                  onClick={() => setVideoItem(item)}
                  aria-label={`Play ${item.fileName}`}
                >
                  <FontAwesomeIcon icon={faVideo} className="h-6 w-6 text-muted-foreground" />
                </button>
              ) : (
                <a
                  href={item.signedUrl}
                  download={item.fileName}
                  className="flex w-full aspect-square items-center justify-center flex-col gap-1 bg-muted/30 hover:bg-muted/60 transition p-2"
                  aria-label={`Download ${item.fileName}`}
                >
                  <FontAwesomeIcon icon={faFile} className="h-5 w-5 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground text-center truncate w-full px-1">
                    {item.fileName}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {fmtBytes(item.fileSize)}
                  </span>
                </a>
              )}

              {canUpload && (
                <>
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const ok = await swalConfirm({
                        title: 'Delete evidence?',
                        text: 'This file will be permanently removed.',
                        confirmText: 'Delete',
                      });
                      if (ok) onDelete(item.id);
                    }}
                    disabled={deletingId === item.id}
                    className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-red-600 transition"
                    aria-label="Delete"
                  >
                    {deletingId === item.id ? (
                      <FontAwesomeIcon icon={faSpinner} className="h-2.5 w-2.5 animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingReplaceId(item.id);
                      replaceRef.current?.click();
                    }}
                    disabled={replacingId === item.id}
                    className="absolute bottom-1 left-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-amber-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-amber-600 hover:text-white transition"
                    aria-label="Replace"
                  >
                    {replacingId === item.id ? (
                      <FontAwesomeIcon icon={faSpinner} className="h-2.5 w-2.5 animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faArrowsRotate} className="h-2.5 w-2.5" />
                    )}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {lightboxId && (
        <EvidenceLightbox items={items} startId={lightboxId} onClose={() => setLightboxId(null)} />
      )}
      {videoItem && <VideoModal item={videoItem} onClose={() => setVideoItem(null)} />}
      <input
        ref={replaceRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleReplaceSelect}
      />
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────

interface Props {
  inspectionId: string;
  stepExecutionId: string;
  evidenceRequired: boolean;
  minPhotos: number;
  minVideos: number;
  minDocuments: number;
  canUpload: boolean;
  isExpanded: boolean;
}

export function StepEvidenceSection({
  inspectionId,
  stepExecutionId,
  evidenceRequired,
  minPhotos,
  minVideos,
  minDocuments,
  canUpload,
  isExpanded,
}: Props) {
  const { data: items = [] } = useStepEvidenceList(inspectionId, stepExecutionId, isExpanded);
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);

  const uploadMutation = useUploadEvidence(inspectionId, stepExecutionId);
  const deleteMutation = useDeleteEvidence(inspectionId, stepExecutionId);
  const replaceMutation = useReplaceEvidence(inspectionId, stepExecutionId);

  async function handleUpload(evidenceType: EvidenceType, file: File) {
    setUploadingType(evidenceType);
    try {
      await uploadMutation.mutateAsync({ evidenceType, file });
      toast.success('Evidence uploaded');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Upload failed'));
    } finally {
      setUploadingType(null);
    }
  }

  async function handleDelete(evidenceId: string) {
    setDeletingId(evidenceId);
    try {
      await deleteMutation.mutateAsync(evidenceId);
      toast.success('Evidence removed');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Delete failed'));
    } finally {
      setDeletingId(null);
    }
  }

  async function handleReplace(evidenceId: string, file: File) {
    setReplacingId(evidenceId);
    try {
      await replaceMutation.mutateAsync({ evidenceId, file });
      toast.success('Evidence replaced');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Replace failed'));
    } finally {
      setReplacingId(null);
    }
  }

  const photos = items.filter((i) => i.evidenceType === 'IMAGE');
  const videos = items.filter((i) => i.evidenceType === 'VIDEO');
  const docs = items.filter((i) => i.evidenceType === 'DOCUMENT');

  const hasAny = items.length > 0;
  const showSection = evidenceRequired || hasAny;

  if (!showSection) return null;

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Evidence
      </h4>
      <div className="space-y-4">
        <EvidenceTypeRow
          label="Photos"
          icon={faImage}
          evidenceType="IMAGE"
          items={photos}
          minCount={minPhotos}
          canUpload={canUpload}
          isCapture={true}
          accept="image/*"
          onUpload={(f) => void handleUpload('IMAGE', f)}
          onDelete={handleDelete}
          onReplace={handleReplace}
          uploadingId={uploadingType}
          deletingId={deletingId}
          replacingId={replacingId}
        />
        <EvidenceTypeRow
          label="Videos"
          icon={faVideo}
          evidenceType="VIDEO"
          items={videos}
          minCount={minVideos}
          canUpload={canUpload}
          isCapture={false}
          accept="video/*"
          onUpload={(f) => void handleUpload('VIDEO', f)}
          onDelete={handleDelete}
          onReplace={handleReplace}
          uploadingId={uploadingType}
          deletingId={deletingId}
          replacingId={replacingId}
        />
        <EvidenceTypeRow
          label="Documents"
          icon={faFile}
          evidenceType="DOCUMENT"
          items={docs}
          minCount={minDocuments}
          canUpload={canUpload}
          isCapture={false}
          accept=".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onUpload={(f) => void handleUpload('DOCUMENT', f)}
          onDelete={handleDelete}
          onReplace={handleReplace}
          uploadingId={uploadingType}
          deletingId={deletingId}
          replacingId={replacingId}
        />
      </div>
    </div>
  );
}
